import { EventSourceMessage, fetchEventSource } from '@microsoft/fetch-event-source'
import { Timeout } from '@tanstack/react-router'
import { PrimitiveAtom, useAtom } from 'jotai'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { currentEnv } from '@/env/vars.ts'
import { queryClient } from '@/QueryClient'
import { debugModeAtom } from '@/store/app/AppAtom.ts'
import {
    chatIdAtom,
    chatProgressMessageAtom,
    chatSettingsAtom,
    createChat,
    getChatDebugData,
    isServerRespondingAtom,
    newQuestionAtom,
    partialMessagesAtom,
    updateChatTimestamp,
    updateConversationSettings,
} from '@/store/gigiChat/ChatAtom'
import { addNotification } from '@/store/gigiChat/Notifications'
import { chatVisualsAtom, getChatVisuals } from '@/store/gigiChat/Visuals'
import { questionActionAtom, selectedLanguagesAtom, translateTextEventsAtom } from '@/store/translate'
import { IMessageVisuals, IProgressEvent, IShortMessageObject } from '@/types/gigiChat/Chat'
import { ICitation, IConversation, IQuestionDisplayData, IStructureAnswer, ITextTranslate } from '@/types/gigiChat/Conversation'
import { IConversationSettings } from '@/types/gigiChat/ConversationSettings'
import { getToken } from '@/utils/fetch'

import Chat from './Chat'

const Container = () => {
    const { t } = useTranslation()
    const [chatId, setChatId] = useAtom<PrimitiveAtom<string>>(chatIdAtom)

    const [chatSettings] = useAtom<PrimitiveAtom<IConversationSettings | null>>(chatSettingsAtom)
    const previousSettings = useRef(chatSettings)
    const [isServerResponding, setIsServerResponding] = useAtom<PrimitiveAtom<boolean>>(isServerRespondingAtom)
    const [partialMessage, setPartialMessage] =
        useAtom<PrimitiveAtom<Array<IShortMessageObject> | Array<IConversation>>>(partialMessagesAtom)
    const [isNewQuestion, setIsNewQuestion] = useState<boolean>(false)
    const [, setChatProgressMessageAtom] = useAtom(chatProgressMessageAtom)
    const [question, setQuestion] = useAtom<PrimitiveAtom<string | null>>(newQuestionAtom)
    const [questionAction, setQuestionAction] = useAtom(questionActionAtom)
    const [, setTranslateEvent] = useAtom(translateTextEventsAtom)
    const [abortController, setAbortController] = useState(new AbortController())
    const [chatvisuals, setChatVisuals] = useAtom<IMessageVisuals | null>(chatVisualsAtom)
    const timeoutRef = useRef<Timeout | undefined>(undefined)
    const [selectedLangs] = useAtom(selectedLanguagesAtom)
    const [gigiFileManagerUrls, setGigiFileManagerUrls] = useState<Array<string>>([])
    const isTranslation = chatSettings?.processingStrategy === 'Translation'
    const isQa = chatSettings?.processingStrategy === 'QA'
    const isAgent = isQa && chatSettings?.processingStrategyQa.mode === 'agent'
    const [debugMode] = useAtom(debugModeAtom)
    const shouldUpload = isAgent || (currentEnv.features.isEnabled('imageUnderstanding') && chatSettings?.processingStrategy === 'Writing')

    const stopStreaming = () => {
        abortController.abort()
        setIsNewQuestion(false)
        setIsServerResponding(false)
        setAbortController(new AbortController())
    }

    const createNewChatTransaction = async () => {
        if (chatSettings?.processingStrategy === 'Translation') {
            setChatId('')
            return createChat('Translation')
        }
        return Promise.resolve()
    }

    const onNewQuestion = (question: string, fileURLManager?: Array<string>) => {
        if (fileURLManager) {
            setGigiFileManagerUrls(fileURLManager)
        } else if (gigiFileManagerUrls.length) {
            setGigiFileManagerUrls([])
        }
        // Only if in QA mode
        if (chatSettings?.processingStrategy === 'QA') {
            // Clear Timeout
            clearTimeout(timeoutRef.current)
            // Set a new one
            const timeoutDelay = sessionStorage.getItem('newChatDelay')
                ? Number(sessionStorage.getItem('newChatDelay'))
                : 1000 * 60 * 60 * 2 // 2h
            const timeoutId = setTimeout(() => {
                void createChat(chatSettings?.processingStrategy)
            }, timeoutDelay)
            // Save it to ref
            timeoutRef.current = timeoutId
        }

        setPartialMessage([
            ...partialMessage,
            {
                question: question,
                answer: null,
                userPromptId: null,
                feedback: null,
                isStreaming: true,
                isError: false,
                instructionalErrorMessageId: null,
                fakeServerMessage: {
                    title: null,
                    message: '',
                },
                structuredAnswer: undefined,
                settings: chatSettings ?? null,
            },
        ])

        updateChatTimestamp()
        setIsNewQuestion(true)
    }

    const handleBeforeUnload = (event: { returnValue: string; preventDefault: () => void }) => {
        if (isServerResponding) {
            event.preventDefault()
            return (event.returnValue = '')
        }
    }

    useEffect(() => {
        if (question) {
            onNewQuestion(question)
            setQuestion(null)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [question])

    useEffect(() => {
        const currentSettings = JSON.parse(JSON.stringify(chatSettings)) as IConversationSettings | null
        return () => {
            previousSettings.current = currentSettings
        }
    }, [chatSettings])

    useEffect(() => {
        window.addEventListener('beforeunload', handleBeforeUnload)
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    })

    const getSettingsData = () => {
        const hasSettingsChanged =
            previousSettings.current &&
            (previousSettings.current.processingStrategy !== chatSettings?.processingStrategy ||
                JSON.stringify(previousSettings.current.processingStrategyQa) !== JSON.stringify(chatSettings.processingStrategyQa) ||
                JSON.stringify(previousSettings.current.processingStrategyWriting) !==
                    JSON.stringify(chatSettings.processingStrategyWriting) ||
                JSON.stringify(previousSettings.current.processingStrategyDocAnalysis) !==
                    JSON.stringify(chatSettings.processingStrategyDocAnalysis) ||
                previousSettings.current.enforceQuotas !== chatSettings.enforceQuotas ||
                previousSettings.current.followUpQuestionStrategy !== chatSettings.followUpQuestionStrategy ||
                previousSettings.current.cachingStrategy !== chatSettings.cachingStrategy ||
                previousSettings.current.processingModePromptLibrary?.metadata !== chatSettings.processingModePromptLibrary?.metadata)
        if (
            hasSettingsChanged ||
            !previousSettings.current ||
            !partialMessage[partialMessage.length - 2].question ||
            chatSettings?.processingStrategy === 'Translation'
        ) {
            return chatSettings
        } else {
            return undefined
        }
    }

    const updateTranslateEvents = (data: ITextTranslate[]) => {
        setTranslateEvent((prevTranslations) => {
            const prevData = (JSON.parse(JSON.stringify(prevTranslations)) as ITextTranslate[]) || []
            let existingIndex = 0

            data.map((event) => {
                const fromLanguage = event.from.languageEnglishName
                const toLanguage = event.to.languageEnglishName

                existingIndex = prevData.findIndex(
                    (item) => item.from.languageEnglishName === fromLanguage && item.to.languageEnglishName === toLanguage,
                )
                if (event.text) {
                    if (existingIndex !== -1) {
                        prevData[existingIndex] = event
                    } else {
                        prevData.push(event)
                    }
                }
            })

            return prevData
        })
    }

    const updateChatVisuals = () => {
        if (chatvisuals?.existingPrompts && chatvisuals?.existingPrompts.length >= 1) {
            const visuals = JSON.parse(JSON.stringify(chatvisuals)) as IMessageVisuals
            visuals.existingPrompts[visuals.existingPrompts.length - 1].isEnd = false
            setChatVisuals(visuals)
        }
    }

    useEffect(() => {
        if (isNewQuestion && partialMessage.length) {
            // Invalidate cache for that specific conversation when asking a new question
            void queryClient.invalidateQueries({ queryKey: [chatId] })
            setIsServerResponding(true)
            const mesageLength: number = partialMessage.length
            const currentQuestionIndex: number = mesageLength ? mesageLength - 1 : mesageLength
            const question: string = partialMessage[currentQuestionIndex].question || ''

            const settingsToSend = getSettingsData()

            if (isTranslation && settingsToSend) {
                settingsToSend.processingStrategyTranslation.languages = selectedLangs.map((lang) => lang.languageCode)
            }

            // this is needded to update the chat visuals when the user is in the prompt library mode and streaming follow up questions
            // previous message visual `isEnd` property has to be manually changed to `false`
            if (previousSettings && previousSettings.current?.processingStrategyWriting.mode === 'promptLibrary') {
                updateChatVisuals()
            }

            const getQuestionDisplayData = (): IQuestionDisplayData => {
                return {
                    displayDataVersion: 'v1',
                    displayData: {
                        fileNames:
                            questionAction === 'QDocTranslate'
                                ? settingsToSend?.processingStrategyTranslation.docs
                                      .filter((doc) => doc.selected === true)
                                      .map((doc) => doc.name)
                                : [],
                        languageNames: selectedLangs.map((lang) => lang.languageEnglishName),
                    },
                }
            }

            setTranslateEvent([])

            void getToken('both').then((res) => {
                const { idToken, accessToken } = res as { idToken: string; accessToken: string }
                const streamUrl = currentEnv.features.isEnabled('gigiCore') ? currentEnv.coreChatUrl : currentEnv.chatUrl
                let newSettings: IConversationSettings | null = null
                void fetchEventSource(`${streamUrl}/chats/${chatId}/stream`, {
                    method: 'POST',
                    body: JSON.stringify({
                        question: question,
                        questionAction: isTranslation || isQa ? questionAction : undefined,
                        settings: settingsToSend,
                        questionDisplayData: isTranslation ? getQuestionDisplayData() : undefined,
                        fileUploads:
                            shouldUpload && gigiFileManagerUrls.length
                                ? { fileUploadType: 'GIGIFILEMANAGER', gigiFileManagerUrls: gigiFileManagerUrls }
                                : undefined,
                    }),
                    credentials: 'include',
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                        'Content-Type': 'application/json',
                        'x-app-identifier': 'gigi',
                        'gsk-apiqw-access-token': accessToken,
                        'enable-agents': String(currentEnv.features.isEnabled('useAgent')),
                    },
                    signal: abortController.signal,
                    openWhenHidden: true,
                    onmessage(msg: EventSourceMessage) {
                        if (msg.event === 'progress') {
                            const progressEvent = JSON.parse(msg.data) as IProgressEvent
                            setChatProgressMessageAtom(progressEvent.suggested_msg)
                        }

                        if (msg.event === 'citations') {
                            const citations: Array<ICitation> = JSON.parse(msg.data) as Array<ICitation>
                            const newPartialMessage = [...partialMessage]
                            newPartialMessage[currentQuestionIndex].citations = citations
                            setPartialMessage(newPartialMessage)
                        }

                        if (msg.event === 'answer') {
                            setChatProgressMessageAtom(null)
                            const parsedData = JSON.parse(msg.data) as { answer: string; error: string; structuredAnswer: IStructureAnswer }

                            const partialAnswer: string = parsedData?.structuredAnswer?.callToAction?.answer || parsedData?.answer

                            const error: string = parsedData?.error
                            if (partialMessage.length) {
                                const newPartialMessage = [...partialMessage]
                                const currentPartialMessage = newPartialMessage[currentQuestionIndex]
                                currentPartialMessage.answer = partialAnswer || error

                                if (parsedData?.structuredAnswer) {
                                    currentPartialMessage.structuredAnswer = parsedData.structuredAnswer

                                    if (parsedData.structuredAnswer.textTranslate) {
                                        const textTranslate = parsedData.structuredAnswer.textTranslate

                                        updateTranslateEvents(textTranslate)
                                    }
                                } else {
                                    currentPartialMessage.structuredAnswer = undefined
                                }
                                if (error) {
                                    currentPartialMessage.isError = true
                                }

                                if (newSettings) {
                                    currentPartialMessage.settings = newSettings
                                }

                                setPartialMessage(newPartialMessage)
                            }
                        }

                        if (msg.event === 'fullAnswer') {
                            const parsedData = JSON.parse(msg.data) as { answer: string; error: string }
                            const { answer, error } = parsedData

                            if (partialMessage.length) {
                                const newPartialMessage = [...partialMessage]
                                const currentPartialMessage = newPartialMessage[currentQuestionIndex]
                                currentPartialMessage.answer = answer || error

                                if (error) {
                                    currentPartialMessage.isError = true
                                }
                                setPartialMessage(newPartialMessage)
                                void getChatVisuals(chatId).then((visuals) => {
                                    setChatVisuals(visuals)
                                })
                            }
                        }

                        if (msg.event === 'instructionalErrorMessageId') {
                            const instructionalError: string = (JSON.parse(msg.data) as { instructionalErrorMessageId: string })
                                ?.instructionalErrorMessageId
                            if (partialMessage.length) {
                                const newPartialMessage = [...partialMessage]
                                newPartialMessage[currentQuestionIndex].instructionalErrorMessageId = instructionalError

                                setPartialMessage(newPartialMessage)
                            }
                        }

                        if (msg.event === 'userPromptId') {
                            const userPromptId: number = Number(msg.data)
                            const newPartialMessage = [...partialMessage]
                            newPartialMessage[currentQuestionIndex].userPromptId = userPromptId
                            setPartialMessage(newPartialMessage)
                        }

                        if (msg.event === 'settings') {
                            const settings = JSON.parse(msg.data) as IConversationSettings
                            updateConversationSettings(chatId, settings, { questionAction, questionDisplayData: getQuestionDisplayData() })

                            const newPartialMessage = JSON.parse(JSON.stringify(partialMessage)) as Array<IConversation>
                            newPartialMessage[currentQuestionIndex].settings = settings
                            //NOTE: usually we should use settings from atom but in this case since the event messages occurs so fast than the new settings are not
                            // updated on the atom, we should find a betted way to handle this
                            newSettings = settings
                            setPartialMessage(newPartialMessage)
                        }

                        if (msg.event === 'end') {
                            const newPartialMessage = JSON.parse(JSON.stringify(partialMessage)) as Array<IConversation>
                            newPartialMessage[currentQuestionIndex].isStreaming = false
                            setPartialMessage(newPartialMessage)

                            setIsNewQuestion(false)
                            setIsServerResponding(false)
                            setChatProgressMessageAtom('')
                            if (isAgent) {
                                //NOTE: we should check if we need this for agents !!!!
                                setQuestionAction(undefined)
                            }

                            const userPromptId = newPartialMessage[currentQuestionIndex]?.userPromptId
                            if (debugMode && userPromptId) {
                                try {
                                    console.info('Waiting for debug data to populate, will fetch in 5 seconds')
                                    // not all debug data is in db by the time we receive the `end` event
                                    setTimeout(() => {
                                        console.info(
                                            `If any data is missing, replay the request in the network tools panel, or call getChatDebugData(${userPromptId}) in the console`,
                                        )
                                        void getChatDebugData(userPromptId).then((debugData) => console.log(debugData))
                                    }, 5000)
                                } catch (e) {
                                    console.log('Failed to debug chat')
                                    console.warn(e)
                                }
                            }
                        }
                    },
                    onerror(err) {
                        setIsNewQuestion(false)
                        setIsServerResponding(false)
                        if (isAgent) {
                            //NOTE: we should check if we need this for agents !!!!
                            setQuestionAction(undefined)
                        }
                        addNotification('error', t('global.error.wrong'))
                        if (err) {
                            throw err // rethrow to stop the operation
                        }
                    },
                })
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNewQuestion])

    return (
        <Chat
            onNewQuestion={onNewQuestion}
            messages={partialMessage}
            onStopStreaming={stopStreaming}
            onCreateNewChat={createNewChatTransaction}
            settings={chatSettings}
            chatId={chatId}
        />
    )
}

export default Container
